/**
 * Słownik obiektów: co stoi na polu.
 *
 * One dictionary on the server, `objects.json`, and this is the shape it
 * arrives in. An object is a thing occupying a cell — a wood, a boulder field,
 * a river running through it, somebody's camp — and it is the vocabulary the
 * placement rules are written in, so that "beside a wood" is an entry in a file
 * rather than a branch in three codebases.
 *
 * Natural objects carry a `from`, naming the world layer that puts them there.
 * Built ones carry none: a building says which object it becomes.
 */

export type Layer = 'forest' | 'stone' | 'river' | 'fish';

export interface From {
  layer: Layer;
  /**
   * Whether the deposit is worked **in the water** rather than on land.
   *
   * Shoals are, everything else is not. Said by the dictionary rather than
   * guessed from the layer's name: every rule about a deposit was written when
   * they were all on dry land, and the fix is for the file to say which element
   * a deposit belongs to instead of the reader keeping a list of exceptions.
   */
  inWater?: boolean;
  /** The level at which the object counts as being there. */
  least: number;
}

export interface ObjectInfo {
  label: string;
  /** Absent for anything a player builds. */
  from?: From;
  /** What working it gives, where that is a thing this object does. */
  yields?: string;
}

export interface Objects {
  version: number;
  objects: Record<string, ObjectInfo>;
}

export const OBJECTS_VERSION = 1;

export async function loadObjects(api: string): Promise<Objects> {
  const response = await fetch(`${api}/api/world/objects`);
  if (!response.ok) {
    throw new Error(`słownik obiektów: ${response.status}`);
  }

  const dictionary = (await response.json()) as Objects;
  if (dictionary.version !== OBJECTS_VERSION) {
    throw new Error(
      `słownik obiektów w wersji ${dictionary.version}, klient czyta ${OBJECTS_VERSION}`
    );
  }
  return dictionary;
}

export function labelOfObject(dictionary: Objects, key: string): string {
  return dictionary.objects[key]?.label ?? key;
}
